import { Alert, Button, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { FormEvent, useState } from 'react';

import { unlockBaseAnalytics } from '../../api/unlockBaseAnalytics';
import { BASE_LABELS } from '../../constants/base';

import storage from '@/utils/storage';

type BaseUnlockModalProps = {
  opened: boolean;
  onUnlocked: () => void;
  onClose: () => void;
};

export const BaseUnlockModal = ({ opened, onUnlocked, onClose }: BaseUnlockModalProps) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setPassword('');
    setError(null);
    onClose();
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const { unlockToken } = await unlockBaseAnalytics(password);
      storage.setAnalyticsBaseUnlockToken(unlockToken);
      setPassword('');
      onUnlocked();
    } catch (err: any) {
      const message = err?.response?.data?.error;
      setError(typeof message === 'string' && message ? message : BASE_LABELS.UNLOCK_ERROR);
      storage.clearAnalyticsBaseUnlockToken();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside
      closeOnEscape
      title={BASE_LABELS.UNLOCK_TITLE}
      centered
    >
      <form onSubmit={onSubmit}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {BASE_LABELS.UNLOCK_HINT}
          </Text>
          {error ? (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          ) : null}
          <PasswordInput
            label={BASE_LABELS.UNLOCK_PASSWORD}
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            autoFocus
            required
          />
          <Button type="submit" loading={isSubmitting} disabled={!password.trim()}>
            {BASE_LABELS.UNLOCK_SUBMIT}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};
